/**
 * Card art URLs from Riftcodex point at Riot Sanity CDN paths (`…/sanity/images/…`).
 * That host accepts Sanity image API query params (`w`, `fm`, etc.) so we avoid
 * shipping full-resolution PNGs in grids and thumbnails.
 *
 * Cloudflare Pages card art is static, so variants are represented by
 * deterministic filenames instead of resize query params.
 */
const SANITY_IMAGES_SEGMENT = '/sanity/images/';
const CLOUDFLARE_CARD_ART_HOST = 'images.riftbound-db.com';

export function isSanityCardArtUrl(url: string): boolean {
  const u = url.trim();
  if (!u.startsWith('http')) return false;
  return u.includes(SANITY_IMAGES_SEGMENT);
}

export function isCloudflareCardArtUrl(url: string): boolean {
  try {
    return new URL(url.trim()).hostname === CLOUDFLARE_CARD_ART_HOST;
  } catch {
    return false;
  }
}

/** Static widths published by the exporter — keep in sync with `scripts/_shared/card-art-variants.mjs`. */
export const CLOUDFLARE_SMALL_WIDTH = 240;
export const CLOUDFLARE_THUMBNAIL_WIDTH = 480;

function cloudflareBaseCardArtPath(pathname: string): string {
  return pathname
    .replace(/\.thumb\.webp$/i, '.webp')
    .replace(/\.sm\.webp$/i, '.webp')
    .replace(/\.detail\.webp$/i, '.webp')
    .replace(/\.(png|jpe?g|avif)$/i, '.webp');
}

const CLOUDFLARE_VARIANT_SUFFIX = {
  detail: '',
  thumbnail: '.thumb',
  small: '.sm',
} as const;

export function cloudflareCardArtUrl(
  url: string,
  variant: 'detail' | 'thumbnail' | 'small',
  language?: string,
): string {
  const trimmed = url.trim();
  if (!trimmed || !isCloudflareCardArtUrl(trimmed)) return trimmed;
  try {
    const parsed = new URL(trimmed);
    const basePath = cloudflareBaseCardArtPath(parsed.pathname);
    const lang = language && language !== 'en' ? language.toLowerCase() : '';
    const suffix = lang ? `.${lang}` : '';
    parsed.pathname = basePath.replace(
      /\.webp$/i,
      `${suffix}${CLOUDFLARE_VARIANT_SUFFIX[variant]}.webp`,
    );
    // Keep a cache-buster (`?v=`) so a replaced file at the same path is fetched
    // again. Drop every other query param; Cloudflare variants are filenames.
    const version = parsed.searchParams.get('v');
    parsed.search = '';
    if (version) parsed.searchParams.set('v', version);
    return parsed.toString();
  } catch {
    return trimmed;
  }
}

/**
 * Both grid-scale Cloudflare widths as a `srcSet`, so the browser can drop to
 * 240px on 1× displays and small slots instead of always pulling 480px.
 *
 * `src` stays the width a 2× display needs, because several callers read only
 * `.src` (WebGL textures, plain `<img src>` uses) and cannot honour `srcSet`.
 */
function cloudflareSrcSetProps(
  url: string,
  sizes: string,
  dpr2TargetPx: number,
  language?: string,
): { src: string; srcSet: string; sizes: string } {
  const small = cloudflareCardArtUrl(url, 'small', language);
  const thumbnail = cloudflareCardArtUrl(url, 'thumbnail', language);
  return {
    src: dpr2TargetPx <= CLOUDFLARE_SMALL_WIDTH ? small : thumbnail,
    srcSet: `${small} ${CLOUDFLARE_SMALL_WIDTH}w, ${thumbnail} ${CLOUDFLARE_THUMBNAIL_WIDTH}w`,
    sizes,
  };
}

export function sanityCardArtUrl(
  url: string,
  opts: { width: number; format?: 'webp' | 'jpg' | 'png' },
): string {
  const trimmed = url.trim();
  if (!trimmed || !isSanityCardArtUrl(trimmed)) return trimmed;
  try {
    const parsed = new URL(trimmed);
    parsed.searchParams.set('w', String(Math.max(16, Math.round(opts.width))));
    if (opts.format) parsed.searchParams.set('fm', opts.format);
    return parsed.toString();
  } catch {
    return trimmed;
  }
}

/** Max width Sanity delivers for standard portrait card art filenames (744×1039). */
const DETAIL_ART_WIDTH = 744;

/** Grid thumbnails: widths for `srcSet` descriptors (cover ~2× typical tile widths). */
const GRID_SRCSET_WIDTHS = [200, 320, 480, 640] as const;

/** Approximate `(container width − chrome) / column count` for default breakpoints. */
const GRID_SRC_SIZES =
  '(max-width: 479px) 45vw, (max-width: 767px) 30vw, (max-width: 991px) 22vw, (max-width: 1279px) 17vw, 200px';

export function gridCardArtImgProps(src: string, language?: string): {
  src: string;
  srcSet?: string;
  sizes?: string;
} {
  const trimmed = src.trim();
  if (isCloudflareCardArtUrl(trimmed)) {
    // Tiles are ~94–210 CSS px, so 2× wants the 480px file; 1× takes 240px.
    return cloudflareSrcSetProps(trimmed, GRID_SRC_SIZES, CLOUDFLARE_THUMBNAIL_WIDTH, language);
  }
  if (!trimmed || !isSanityCardArtUrl(trimmed)) {
    return { src: trimmed };
  }
  const srcSet = GRID_SRCSET_WIDTHS.map(
    (w) => `${sanityCardArtUrl(trimmed, { width: w, format: 'webp' })} ${w}w`,
  ).join(', ');
  return {
    src: sanityCardArtUrl(trimmed, { width: GRID_SRCSET_WIDTHS[GRID_SRCSET_WIDTHS.length - 1], format: 'webp' }),
    srcSet,
    sizes: GRID_SRC_SIZES,
  };
}

/**
 * Detail URL for a printed face. A gallery URL is rewritten onto the English
 * file's language sibling (`001.webp` → `001.ko.webp`) so the page never
 * loads Riot's CDN. A face that is already on our host is kept.
 */
export function ownLocaleFaceUrl(
  englishUrl: string | undefined,
  localeUrl: string | undefined,
  language: string,
): string | undefined {
  const locale = localeUrl?.trim();
  if (!locale) return undefined;
  if (isCloudflareCardArtUrl(locale)) return locale;
  if (!isSanityCardArtUrl(locale)) return locale;
  const english = englishUrl?.trim();
  if (english && isCloudflareCardArtUrl(english)) {
    return cloudflareCardArtUrl(english, 'detail', language);
  }
  return undefined;
}

export function detailCardArtUrl(src: string, language?: string): string {
  const trimmed = src.trim();
  if (isCloudflareCardArtUrl(trimmed)) return cloudflareCardArtUrl(trimmed, 'detail', language);
  if (!trimmed || !isSanityCardArtUrl(trimmed)) return trimmed;
  return sanityCardArtUrl(trimmed, { width: DETAIL_ART_WIDTH, format: 'webp' });
}

/** Search autocomplete: tiny slot; keep bytes low but match DPR via `sizes` + `srcSet`. */
const TYPEAHEAD_SRCSET_WIDTHS = [72, 108, 160] as const;

/**
 * Responsive WebP URLs for dropdown card thumbnails.
 * Pass the rendered artwork width in CSS px so the browser picks the right source.
 */
export function typeaheadCardArtImgProps(
  src: string,
  slotWidthCssPx: number,
): { src: string; srcSet?: string; sizes?: string } {
  const trimmed = src.trim();
  if (isCloudflareCardArtUrl(trimmed)) {
    return cloudflareSrcSetProps(trimmed, `${Math.ceil(slotWidthCssPx)}px`, slotWidthCssPx * 2);
  }
  if (!trimmed || !isSanityCardArtUrl(trimmed)) {
    return { src: trimmed };
  }
  const srcSet = TYPEAHEAD_SRCSET_WIDTHS.map(
    (w) => `${sanityCardArtUrl(trimmed, { width: w, format: 'webp' })} ${w}w`,
  ).join(', ');
  return {
    src: sanityCardArtUrl(trimmed, { width: TYPEAHEAD_SRCSET_WIDTHS[TYPEAHEAD_SRCSET_WIDTHS.length - 1], format: 'webp' }),
    srcSet,
    sizes: `${slotWidthCssPx}px`,
  };
}

/**
 * Tiny on-screen slots with variable CSS size (e.g. PDF preview tiles).
 * Uses the long edge (or max of w/h) for `sizes`; descriptors scale with DPR.
 */
export function slotCardArtImgProps(
  src: string,
  slotLongEdgeCssPx: number,
): { src: string; srcSet?: string; sizes?: string } {
  const trimmed = src.trim();
  if (isCloudflareCardArtUrl(trimmed)) {
    const slot = Math.max(24, slotLongEdgeCssPx);
    return cloudflareSrcSetProps(trimmed, `${Math.ceil(slot)}px`, slot * 2);
  }
  if (!trimmed || !isSanityCardArtUrl(trimmed)) {
    return { src: trimmed };
  }
  const base = Math.max(24, slotLongEdgeCssPx);
  const d1 = Math.min(640, Math.max(72, Math.round(base * 1.5)));
  const d2 = Math.min(640, Math.max(96, Math.round(base * 2)));
  const d3 = Math.min(640, Math.max(128, Math.round(base * 3)));
  const widths = [...new Set([d1, d2, d3])].sort((a, b) => a - b);
  const largest = widths[widths.length - 1] ?? d3;
  const srcSet = widths
    .map((w) => `${sanityCardArtUrl(trimmed, { width: w, format: 'webp' })} ${w}w`)
    .join(', ');
  return {
    src: sanityCardArtUrl(trimmed, { width: largest, format: 'webp' }),
    srcSet,
    sizes: `${Math.ceil(base)}px`,
  };
}

/** Decorative / low-opacity fills: one light WebP, no srcSet (CSS background). */
export function decorativeCardArtUrl(src: string, widthPx = 420): string {
  const trimmed = src.trim();
  if (!trimmed) return trimmed;
  if (isCloudflareCardArtUrl(trimmed)) {
    // Blurred or heavily faded, so the small variant is enough well past 240px.
    const variant = widthPx > CLOUDFLARE_THUMBNAIL_WIDTH ? 'thumbnail' : 'small';
    return cloudflareCardArtUrl(trimmed, variant);
  }
  return sanityCardArtUrl(trimmed, { width: Math.max(64, Math.round(widthPx)), format: 'webp' });
}
